import React from "react";
import FormInput from "Common/Custom/FormInput";
import FormInputFile from "Common/Custom/FormInputFile";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import Switch from "Common/Custom/Switch";
import FormSelect from "Common/Custom/FormSelect";
import FormPhoneInput from "Common/Custom/FormPhoneInput";
import FormDateInput from "../../FormDatePicker";
import FormTextarea from "Common/Custom/FormTextarea";
import CountryName from "Common/json/CountryName.json";
import { useLocationCascading } from "Hooks/useLocationCascading";

const formatSectionTitle = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const AddCustomerForm = ({
  fields = {},
  values = {},
  errors = {},
  handleChange = () => { },
  handleFileChange = () => { },
  handleSubmit = () => { },
  loading = false,
  filePreviews = {},
}) => {
  const countryOptions = CountryName.map((country) => ({
    value: country.name,
    label: country.name,
  }));

  /*
   * =====================================================
   * COUNTRY
   * =====================================================
   */

  const selectedCountry = String(values.country || "")
    .trim()
    .toUpperCase();

  const hasCountry = Boolean(selectedCountry);
  const isIndia = selectedCountry === "INDIA";

  /*
   * =====================================================
   * LOCATION
   * =====================================================
   */

  const {
    states,
    districts,
    blocks,
    loadingStates,
    loadingDistricts,
    loadingBlocks,
  } = useLocationCascading(values);

  const stateOptions = states.map((state) => ({
    value: state.state_code,
    label: state.state_name,
  }));

  const districtOptions = districts.map((district) => ({
    value: district.district_code,
    label: district.district_name,
  }));

  const blockOptions = blocks.map((block) => ({
    value: block.block_code,
    label: block.block_name,
  }));

  const renderField = (field) => {
    /*
     * =====================================================
     * IDENTITY DETAILS
     * =====================================================
     */





    /*
     * =====================================================
     * STATE
     * =====================================================
     */

    if (field.name === "state") {
      // Country not selected
      if (!hasCountry) {
        return (
          <FormInput
            key={field.name}
            field={{
              ...field,
              type: "text",
              disabled: true,
              placeholder: "Select Country First",
            }}
            value=""
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );
      }

      // India -> Select State
      if (isIndia) {
        return (
          <FormSelect
            key={field.name}
            field={{
              ...field,
              label: "State",
              placeholder: loadingStates
                ? "Loading States..."
                : "Select State",
            }}
            value={values[field.name] || ""}
            handleChange={handleChange}
            options={stateOptions}
            error={errors[field.name]}
          />
        );
      }

      // Foreign -> Manual State
      return (
        <FormInput
          key={field.name}
          field={{
            ...field,
            type: "text",
            placeholder: "Enter State",
          }}
          value={values[field.name] || ""}
          handleChange={handleChange}
          error={errors[field.name]}
        />
      );
    }

    /*
     * =====================================================
     * CITY / DISTRICT
     * =====================================================
     */

    if (field.name === "city") {
      // Country not selected
      if (!hasCountry) {
        return (
          <FormInput
            key={field.name}
            field={{
              ...field,
              type: "text",
              disabled: true,
              placeholder: "Select Country First",
            }}
            value=""
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );
      }

      // India -> Select District
      if (isIndia) {
        return (
          <FormSelect
            key={field.name}
            field={{
              ...field,
              label: "District / City",
              placeholder: loadingDistricts
                ? "Loading Districts..."
                : "Select District / City",
            }}
            value={values[field.name] || ""}
            handleChange={handleChange}
            options={districtOptions}
            error={errors[field.name]}
          />
        );
      }

      // Foreign -> Manual City
      return (
        <FormInput
          key={field.name}
          field={{
            ...field,
            type: "text",
            placeholder: "Enter District / City",
          }}
          value={values[field.name] || ""}
          handleChange={handleChange}
          error={errors[field.name]}
        />
      );
    }

    /*
     * =====================================================
     * BLOCK / TEHSIL
     * =====================================================
     */

    if (field.name === "block") {
      // Country not selected
      if (!hasCountry) {
        return (
          <FormInput
            key={field.name}
            field={{
              ...field,
              type: "text",
              disabled: true,
              placeholder: "Select Country First",
            }}
            value=""
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );
      }

      // India -> Select Block
      if (isIndia) {
        return (
          <FormSelect
            key={field.name}
            field={{
              ...field,
              label: "Block / Tehsil",
              placeholder: loadingBlocks
                ? "Loading Blocks..."
                : "Select Block / Tehsil",
            }}
            value={values[field.name] || ""}
            handleChange={handleChange}
            options={blockOptions}
            error={errors[field.name]}
          />
        );
      }

      // Foreign -> Manual Block
      return (
        <FormInput
          key={field.name}
          field={{
            ...field,
            type: "text",
            placeholder: "Enter Block / Tehsil",
          }}
          value={values[field.name] || ""}
          handleChange={handleChange}
          error={errors[field.name]}
        />
      );
    }

    /*
     * =====================================================
     * NORMAL FIELDS
     * =====================================================
     */

    switch (field.type) {
      case "file":
        return (
          <FormInputFile
            key={field.name}
            field={field}
            handleChange={handleFileChange}
            error={errors[field.name]}
            imagePreview={filePreviews[field.name] || ""}
          />
        );

      case "switch":
        return (
          <Switch
            key={field.name}
            values={values[field.name]}
            handleChange={handleChange}
            field={field}
            ValueOne={field.ValueOne || "active"}
            ValueTwo={field.ValueTwo || "inactive"}
            LabelOne={field.LabelOne || "Active"}
            LabelTwo={field.LabelTwo || "Inactive"}
          />
        );

      case "select":
        return (
          <FormSelect
            key={field.name}
            field={field}
            value={values[field.name] || ""}
            handleChange={handleChange}
            options={
              field.name === "country"
                ? countryOptions
                : field.options
            }
            error={errors[field.name]}
          />
        );

      case "tel":
        return (
          <FormPhoneInput
            key={field.name}
            field={field}
            value={values[field.name] || ""}
            country_code={values.country_dial_code}
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );

      case "date":
        return (
          <FormDateInput
            key={field.name}
            name={field.name}
            label={field.label}
            value={
              values[field.name] || ""
            }
            onChange={handleChange}
            error={errors[field.name]}
            required={field.required}
            placeholder={
              field.placeholder ||
              "DD-MM-YYYY"
            }
            maxDate={new Date()}
            className={
              field.fieldSize ||
              "col-md-6 col-lg-4 col-12"
            }
          />
        );

      case "textarea":
        return (
          <FormTextarea
            key={field.name}
            field={field}
            value={values[field.name] || ""}
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );

      default:
        return (
          <FormInput
            key={field.name}
            field={field}
            value={values[field.name] || ""}
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );
    }
  };

  const renderSection = (sectionKey) => {
    const sectionFields = fields[sectionKey] || [];

    if (!sectionFields.length) {
      return null;
    }

    return (
      <div className="mb-4" key={sectionKey}>
        <h5 className="mb-3">
          {formatSectionTitle(sectionKey)}
        </h5>

        <div className="row g-3">
          {sectionFields.map((field) => {
            const updatedField =
              sectionKey === "identity_details" &&
                field.name === "pid_type"
                ? {
                  ...field,
                }
                : field;

            return renderField(updatedField);
          })}
        </div>
      </div>
    );
  };

  return (
    <PageBody
      onSubmit={handleSubmit}
      headerchildren={<HeadingOne>Create Customer</HeadingOne>}
    >
      {Object.keys(fields)
        .filter((key) => key !== "tabledata")
        .map(renderSection)}

      <FormSubmitButton
        label="Create"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

export default AddCustomerForm;
